import { LocalizedString, ValidationError } from '@onrunning/backstage-models';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { globalConstants } from '../constants/global.constants';
import { useUserStore } from '../stores/user';
import { toISOString } from '../utils/temporalUtils';
import { isString } from './is-string';

export function useFormat() {
  const { userLocale } = storeToRefs(useUserStore());
  const { t } = useI18n();

  function formatTimeForTwoDigits(time: number): string {
    return time.toString().padStart(2, '0');
  }

  function formatDateToLocaleStringIgnoringTime(date: Date | string) {
    const dateString = toISOString(date);

    return formatDate(dateString, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  function formatNotificationDate(dateString: string) {
    const date = new Date(dateString);

    return new Intl.DateTimeFormat(userLocale.value, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    }).format(date);
  }

  function formatDate(
    inputDate: Date | string,
    options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'UTC',
    },
  ) {
    const date = isString(inputDate) ? new Date(inputDate) : inputDate;

    if (date && Object.prototype.toString.call(date) === '[object Date]') {
      if (!isNaN(date.getTime())) {
        return new Intl.DateTimeFormat(userLocale.value, options).format(date);
      }
    }

    return '';
  }

  function formatDateNoUTC(date: Date | string) {
    return formatDate(date, { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  function formatCurrency(currencyCode: string | undefined, value: any, ignoreZero = true) {
    if (!currencyCode) {
      return '';
    }

    if (value || (value === 0 && !ignoreZero)) {
      try {
        return new Intl.NumberFormat(userLocale.value, {
          style: 'currency',
          currency: currencyCode,
          currencyDisplay: 'code',
          currencySign: 'accounting',
        } as any).format(value);
      } catch (err) {
        // default fallback format for buggy safari 14.1.x users
        return `${currencyCode} ${value}`;
      }
    }

    return '';
  }

  function currencyComparator(a: number, b: number) {
    // We want currencies to be sorted by value, disregarding
    // currency. The `|| 0` is for undefined and NaN values, of which
    // for some reason we have some.
    return (a || 0) - (b || 0);
  }

  function formatDateToIsoStringIgnoringTime(date: Date | string) {
    return toISOString(date);
  }

  function formatNumber(value: any) {
    if (!value) {
      return '-';
    }

    return value;
  }

  function formatPercentage(value: number, ignoreZero = false) {
    if (value || (value === 0 && !ignoreZero)) {
      return `${(value * 100).toFixed(2)}%`;
    }

    return '';
  }

  function gridNumberFormatter(params: any): string {
    return formatNumber(params.value).toString();
  }

  function gridDateFormatter(params: any): string {
    return formatDateNoUTC(params.value);
  }

  function getTranslationFromTranslateField(localizedString: LocalizedString): string {
    const empty = '-';

    return (
      (localizedString as any)[userLocale.value] ?? (localizedString as any)[globalConstants.fallBackLocale] ?? empty
    );
  }

  function launchCalendarDateFormatter(date: string): string {
    return formatDate(date, { day: 'numeric', month: 'short' });
  }

  function formatGridNumberColumn(params: any): string {
    const parsedNumber = parseInt(params.value, 10);

    return isNaN(parsedNumber) ? '-' : parsedNumber.toString();
  }

  function formatNonLocalisedDateString(date: Date | string = new Date()) {
    return toISOString(date);
  }

  function parseAndFormatTimestampInUtc(timestamp: string) {
    return toISOString(timestamp);
  }

  /**
   * Parse a UTC date to the same date in local timezone.
   * Example "2021-09-02T00:00:00.000Z" will be parsed to "2021-09-02" in the local timezone.
   * @param date Date to parse, probably in UTC
   * @returns Formatted date in local timezone with the correct locale format
   */
  function parseUtcDateToLocaleDate(date: string) {
    return formatDateNoUTC(parseAndFormatTimestampInUtc(date));
  }

  const getFormattedCountryCode = (countryCode: string, countryName: string): string => {
    return `${countryCode}${globalConstants.countryCodeAndCountryDelimiter}${countryName}`;
  };

  const getSplittedCountryCode = (formattedCountryCode: string) => {
    return formattedCountryCode ? formattedCountryCode.split(globalConstants.countryCodeAndCountryDelimiter) : [];
  };

  const formatError = (errors: ValidationError[]): string => {
    return errors.map((e) => t(e.error, e.injectValues ?? {})).join(', ');
  };

  return {
    formatTimeForTwoDigits,
    formatCurrency,
    currencyComparator,
    formatDate,
    formatDateNoUTC,
    formatDateToIsoStringIgnoringTime,
    formatDateToLocaleStringIgnoringTime,
    formatNotificationDate,
    formatNumber,
    formatPercentage,
    gridNumberFormatter,
    gridDateFormatter,
    getTranslationFromTranslateField,
    launchCalendarDateFormatter,
    formatGridNumberColumn,
    formatNonLocalisedDateString,
    parseUtcDateToLocaleDate,
    getFormattedCountryCode,
    getSplittedCountryCode,
    formatError,
  };
}
